import { Component, Inject, NgZone, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import firebase from 'firebase/app';
import { LogLevelsDictionary } from 'src/app/dictionaries/LogLevels';
import { UserPlans } from 'src/app/dictionaries/UserPlans';
import { UserRoles } from 'src/app/dictionaries/UserRoles';

import { Labels } from 'src/app/dictionaries/labels';
import { LogService } from 'src/app/services/log.service';

import { DialogService } from 'src/app/services/dialog.service';
import { UIMessagingService } from 'src/app/services/uimessaging.service';
import { environment } from 'src/environments/environment';
import { AuthService } from '../../services/auth.service';
import { SearchService } from '../../services/search.service';
import { WindowsManagerService } from '../../services/windows-manager.service';
import { EditClientFormComponent } from '../edit-client-form/edit-client-form.component';
import { DialogData } from '../ui/optionChooser/optionChooser.component';
import { ClientProfileComponent } from './../clientprofile/clientprofile.component';

const dbPatients = firebase.firestore().collection('patients');
const allowedUsers = [UserRoles.admin, UserRoles.associate, UserRoles.owner, UserRoles.superuser, UserRoles.consultant];

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-search',
  styleUrls: ['./search.component.scss'],
  templateUrl: './search.component.html',
  standalone: false
})
export class SearchComponent implements OnInit {
  Labels = Labels;
  selectedOwner: any;
  ownerNeeded = false;
  nameSearch: string;
  searchResult: {
    firstname: string;
    lastname: string;
    caseName: string;
    gender: string;
    dob: string;
    patientIDs: Array<{ value: string; facility: string }>;
    hasNotifications: boolean;
  }[] = [];
  patientIdSearch: string;
  caseName: string;
  fileName: string;
  fileID: string;
  fileUploadUser: string;
  currentUser: firebase.User;
  data: any;
  userData: { realRole?: string; user?: any; filesSharedWith?: any; caseName?: string; uid?: string };
  UserRoles: { owner: string; admin: string; associate: string; consultant: string; superuser: string };
  ownerID: any;
  originalData: any[];
  savedData: any[];
  ableToEditClient = false;
  clientsWithNotifications: any[];
  owners = [
    { value: 'owner-0', label: 'Owner 1' },
    { value: 'owner-1', label: 'Owner 2' },
    { value: 'owner-2', label: 'Owner 3' },
    { value: 'owner-3', label: 'Owner 4' },
    { value: 'owner-4', label: 'Owner 5' }
  ];
  allowedCases: any[];
  patientType: any;
  title: string;
  pendingMergeCaseCount = 0;
  isHubStaff = false;
  canViewMergeCases = false;

  constructor(
    public search_$: SearchService,
    public ng: NgZone,
    private dialog_$: DialogService,
    public route: ActivatedRoute,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<SearchComponent>,
    private log_$: LogService,
    public auth_$: AuthService,
    private uiMessaging_$: UIMessagingService,
    private windowsManager_$: WindowsManagerService,
    public router: Router,
    @Inject(MAT_DIALOG_DATA) public dialogdata: DialogData
  ) {
    this.UserRoles = {
      owner: UserRoles.owner,
      admin: UserRoles.admin,
      associate: UserRoles.associate,
      consultant: UserRoles.consultant,
      superuser: UserRoles.superuser
    };
    this.data = [];
    this.nameSearch = '';
    this.patientIdSearch = '';
    this.caseName = '';
    this.fileName = '';
    this.fileID = '';
    this.fileUploadUser = '';
    this.ownerID = this.dialogdata['ownerID'];
    this.patientType = this.dialogdata['type'];
  }

  onClose(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
    this.router.navigateByUrl('/');
  }

  isOwner() {
    return this.auth_$.userData.getValue()['realRole'] === UserRoles.owner;
  }

  getTitle() {
    return (this.patientType === 'external' ? 'Hospital ' : 'Employee ') + this.Labels.clientMatterLabel + ' List';
  }

  async ngOnInit() {
    this.title = this.getTitle();

    // NOTE - Subscribe to the user data
    const subscription3 = this.auth_$.user.subscribe({ next: u => (this.currentUser = u) });

    // NOTE - Subscribe to the userData
    const subscription = this.auth_$.userData.subscribe({
      next: async userdata => {
        const keyCount = Object.keys(userdata || {}).length;
        const ud = userdata as { realRole?: string; role?: string };
        const realRole = ud?.realRole ?? ud?.role;
        if (keyCount > 0) {
          this.userData = userdata;

          const roleForBranch = this.userData.realRole ?? (this.userData as { role?: string }).role;
          const isConsultant = roleForBranch === UserRoles.consultant;
          this.ableToEditClient = [UserRoles.admin, UserRoles.owner, UserRoles.associate].includes(roleForBranch);

          if (isConsultant) {
            //NOTE - User role is consultant
            this.ownerNeeded = false;

            // NOTE - Get all patients for the consultant
            const allowedPatients = await this.getAllowedPatients(
              realRole,
              this.userData.filesSharedWith,
              this.ownerID
            );
            this.allowedCases = Array.from(new Set(allowedPatients));

            // NOTE - Get all patients for the consultant
            this.getAllPatients(
              true,
              String(roleForBranch || ''),
              this.userData.user.email.toLowerCase(),
              this.allowedCases,
              this.patientType
            );
          }
          // NOTE - Else we can get the list of patients directly
          else {
            const authUidFromAuth = this.auth_$.auth?.currentUser?.uid;
            const authUidFromUser = this.currentUser?.uid;
            const authUid = authUidFromAuth ?? authUidFromUser ?? this.ownerID;
            if (!authUid) {
              console.warn('SearchComponent: No auth UID available for query');
              this.searchResult = [];
              this.originalData = [];
            } else {
              const ud = this.userData as any;
              const isFacilityUser = ud?.facilityuser && ud?.facility;
              const isOwnerRole = roleForBranch === UserRoles.owner;
              const isHub = ud?.plancode?.includes(UserPlans.platinum) && ud?.facility;

              const isStaffRole = roleForBranch === UserRoles.owner || roleForBranch === UserRoles.admin;
              const isSpoke = ud?.plancode === UserPlans.spoke && ud?.facility;

              // Hub staff flag — used for Hub-only features (e.g. auto-forwarding section)
              if (isHub && isStaffRole) {
                this.isHubStaff = true;
              }

              // Merge cases badge — visible to Hub AND Spoke owners/admins
              if (isStaffRole && (isHub || isSpoke)) {
                this.canViewMergeCases = true;
                const facility = (ud.facility || '').toLowerCase();
                firebase.firestore()
                  .collection('dicomMergeCases')
                  .where('status', '==', 'pending')
                  .where('incomingFacilityId', '==', facility)
                  .get()
                  .then(snap => { this.pendingMergeCaseCount = snap.size; })
                  .catch(() => { /* non-blocking */ });
              }

              const baseQuery = isFacilityUser
                ? dbPatients.where(`sharedWithFacilityMap.${ud.facility.toLowerCase()}`, '==', true)
                : isOwnerRole
                  ? this.getPatientsByOwnerUID(authUid)
                  : this.getAllPatientsByOwnerID(authUid);

              const firebaseUser = this.auth_$.auth?.currentUser;
              const tokenReady = firebaseUser ? firebaseUser.getIdToken(false) : Promise.resolve(null);
              tokenReady.then(async () => {
                try {
                  let docs: firebase.firestore.QueryDocumentSnapshot[];

                  // Hub non-facility-users: merge own patients with Spoke-network patients
                  // so that patients created by Spokes (ownerID != hubUID) are also visible.
                  if (!isFacilityUser && isHub) {
                    const networkQuery = dbPatients.where(
                      `sharedWithFacilityMap.${ud.facility.toLowerCase()}`, '==', true
                    );
                    const [ownSnapshot, networkSnapshot] = await Promise.all([
                      baseQuery.get(),
                      networkQuery.get()
                    ]);
                    const allDocs = new Map<string, firebase.firestore.QueryDocumentSnapshot>();
                    ownSnapshot.docs.forEach(d => allDocs.set(d.id, d));
                    networkSnapshot.docs.forEach(d => allDocs.set(d.id, d));
                    docs = Array.from(allDocs.values());
                  } else {
                    const snapshot = await baseQuery.get();
                    docs = snapshot.docs;
                  }

                  const filtered = this.patientType
                    ? docs.filter(doc => doc.data()['type'] === this.patientType)
                    : docs;
                  this.searchResult = this.originalData = filtered
                    .map(doc => this.buildClientObject2(doc.data(), doc.id))
                    .sort(this.sortByCreatedAtDesc);
                } catch (err: any) {
                  console.error('SearchComponent: Patient list load error', err?.code, err?.message);
                  this.searchResult = [];
                  this.originalData = [];
                }
              }).catch(tokenErr => {
                console.error('SearchComponent: Failed to get auth token', tokenErr);
                this.searchResult = [];
                this.originalData = [];
              });
            }
          }
        }
      },
    });

    const subscription2 = this.search_$.searchResults.subscribe({
      next: results => this.populateSearchResults(results),
      error: err => console.error(err)
    });

    return () => {
      subscription.unsubscribe();
      subscription2.unsubscribe();
      subscription3.unsubscribe();
    };
  }

  /**
   * Get all patients by owner ID and store the results in the originalData and searchResult.
   * @param ownerID - The owner ID.
   * @param type - The type of the patient.
   * @returns
   */
  private getPatientsByOwnerUID(ownerUID: string, type?: string) {
    if (type) return dbPatients.where('ownerID', '==', ownerUID).where('type', '==', type);
    else return dbPatients.where('ownerID', '==', ownerUID);
  }

  private getAllPatientsByOwnerID(ownerID: string, type?: string) {
    if (type) return dbPatients.where('owners', 'array-contains', ownerID).where('type', '==', type);
    else return dbPatients.where('owners', 'array-contains', ownerID);
  }

  handleSelectClient(item) {
    if (!item.caseName) {
      console.warn('SearchComponent: No caseName found for item:', item);
      this.uiMessaging_$.toastMessage('Patient case name not found. Please contact support.', 'ALERT');
      return;
    }

    if (this.dialogRef) {
      this.dialogRef.close({ navigated: true });
    }

    // Navigate to patient overview route
    this.router
      .navigate(['/patient-overview', item.caseName], {
        skipLocationChange: false
      })
      .then(() => {
        // Close the sidebar menu after navigation completes
        if (this.auth_$.drawer) {
          this.auth_$.drawer.close();
        }
      });
  }

  editClient(item: any) {
    const userData = this.auth_$.userData.getValue() as any;
    const config = new MatDialogConfig();
    config.autoFocus = true;
    config.width = '600px';
    config.data = {
      DateOfBirth: item.dob,
      FirstName: item.firstname,
      LastName: item.lastname,
      caseName: item.caseName,
      patientDocId: item.patientDocId || item.caseName,
      metadata: item.metadata,
      userFacility: userData.facility || '',
      ownerID: item.ownerID || userData.ownerID || userData.uid,
      patientPicture: item.patientPicture || item.metadata?.patientPicture || ''
    };

    const dialogRef = this.dialog.open(EditClientFormComponent, config);
    dialogRef.afterClosed().subscribe(result => { if (result === 1) this.ngOnInit(); });
  }

  getOwnerIdByRole(role): string {
    return role === UserRoles.owner ? this.currentUser.uid : this.auth_$.userData.getValue()['ownerID'];
  }

  private async filterAllowedPatientsByOwnerID(allowedPatients: string[], ownerID: string) {
    const patients = [];

    for (const patient of allowedPatients) {
      const res = await this.patientBelongsToOwner(patient, ownerID);
      if (res) patients.push(patient);
    }

    return patients;
  }

  private patientBelongsToOwner(patient, ownerID) {
    return firebase
      .firestore()
      .collection('patients')
      .where('caseName', '==', patient)
      .where('forDelete', '==', false)
      .where('ownerID', '==', ownerID)
      .limit(1)
      .get()
      .then(querySnapshot => {
        if (querySnapshot.empty) {
          return false;
        } else {
          return true;
        }
      });
  }

  async getAllowedPatients(realRole: string, filesSharedWith: any, ownerID: string) {
    let allowedPatients = [];

    switch (realRole) {
      case UserRoles.consultant:
        allowedPatients = [...new Set(filesSharedWith.map(currentvalue => currentvalue.patientCaseName))];
        allowedPatients = await this.filterAllowedPatientsByOwnerID(allowedPatients, ownerID);
        break;
      case UserRoles.owner:
      case UserRoles.associate:
      case UserRoles.admin:
        const casesOwneredDocs = (await this.getAllPatientsByOwnerID(ownerID).get()).docs;
        casesOwneredDocs.forEach(item => allowedPatients.push(item.data().caseName));
        break;
      default:
        break;
    }
    return allowedPatients;
  }

  refreshCurrentUserSharedFiles() {
    return this.auth_$.userReady(this.auth_$.userData.getValue()['user'], 'refreshCurrentUserSharedFiles');
  }

  async getFilesSharedWith() {
    await this.refreshCurrentUserSharedFiles();
    const dbUser = this.auth_$.userData.getValue();
    const allowedPatients = await this.getAllowedPatients(dbUser['role'], dbUser['filesSharedWith'], this.ownerID);
    this.ownerID = (await this.auth_$.getOwnerID(this.auth_$.uid)) || this.auth_$.uid;
    const isOwner = dbUser['role'] === UserRoles.owner;
    const isConsultant = dbUser['role'] === UserRoles.consultant;
    if (!isOwner) {
      this.getAllPatients(isConsultant, dbUser['role'], dbUser['email'].toLowerCase(), allowedPatients);
    }
  }

  matchSearchCriteria(item, text) {
    const criterias = ['FirstName', 'LastName', 'caseName', 'LegalCaseId', 'DateOfBirth'];
    for (const criteria of criterias) {
      if (item[criteria] && item[criteria].toLowerCase().indexOf(text.toLowerCase()) > -1) {
        return true;
      }
    }
    return false;
  }

  search(event) {
    this.searchResult = this.originalData;
    if (event.target.value && event.target.value.length > 0) {
      this.searchResult = this.searchResult.filter(item => this.matchSearchCriteria(item, event.target.value));
    }
  }

  searchRefine() {
    let filter = data => true;

    if (this.patientIdSearch.trim() !== '') {
      filter = data => data['PatientId'] === this.patientIdSearch;
    }

    if (this.nameSearch.trim() !== '') {
      filter = data => data['LastName'].toLowerCase() === this.nameSearch.toLowerCase();
    }

    this.searchResult = this.searchResult.filter(filter);
  }

  routeToProfile(patient) {
    this.log_$.storeLog(
      this.auth_$.userData.getValue(),
      LogLevelsDictionary.info,
      { message: 'A user has visited a client profile.', user: this.userData.user.uid },
      'ViewClientProfile',
      patient.caseName
    );

    if (!patient.ownerID) {
      this.uiMessaging_$.toastMessage(
        `This profile does not have a valid ownerID, please contact support before retry.`,
        'ALERT'
      );
      return;
    }

    const isSpoke = this.auth_$.userData.getValue()['plancode'] === UserPlans.spoke;

    if (!this.windowsManager_$.clientProfileOpened) {
      this.windowsManager_$.profileDialogRef = this.dialog_$.open(
        isSpoke ? ClientProfileComponent : ClientProfileComponent,
        {
          width: '100vw',
          data: { patient }
        }
      );
    }
  }

  async getInitialData() {
    return (await this.getAllPatientsByOwnerID(this.ownerID).get()).docs
      .map(doc => this.buildClientObject2(doc.data(), doc.id))
      .sort(this.sortByCreatedAtDesc);
  }

  private sortByCreatedAtDesc(a: any, b: any): number {
    const aTime = a.createdAt ? (a.createdAt as Date).getTime() : 0;
    const bTime = b.createdAt ? (b.createdAt as Date).getTime() : 0;
    if (bTime !== aTime) return bTime - aTime;
    return a.hasNotifications < b.hasNotifications ? 1 : -1;
  }

  private buildClientObject2(patient, docid) {
    // Derive name parts and DOB from multiple possible sources
    const [firstFromName, ...restOfName] = (patient.name || '').toString().trim().split(/\s+/);

    return {
      caseName: patient.caseName || '',
      origin: patient.origin || '',
      dob: this.formatDate(patient.DateOfBirth || patient?.metadata?.dob || patient?.metadata?.date_of_birth),
      defaultFolders: patient.defaultFolders || [],
      firstname: this.toCapitalCase(
        patient.FirstName || patient?.metadata?.firstName || this.toCapitalCase(firstFromName || '')
      ),
      lastname: this.toCapitalCase(
        patient.LastName || patient?.metadata?.lastName || this.toCapitalCase(restOfName.join(' ') || '')
      ),
      clioMatterId: patient?.clioMatterId ?? patient?.clioDocId ?? '',
      name: patient.name || '',
      clientUser: patient.clientUser || '',
      legalCaseId: patient.LegalCaseId || '',
      ownerID: patient.ownerID || '',
      owners: patient.ownerID ? [patient.ownerID] : [],
      patientDocId: docid || '',
      sharedUserFiles: patient.sharedUserFiles || [],
      createdAt: patient.createdAt?.toDate?.() ?? (patient.createdAt instanceof Date ? patient.createdAt : null),
      hasNotifications: this.hasNotifications(patient.caseName),
      restrictedProfiles: patient.restrictedProfiles || [],
      allowedProfiles: patient.allowedProfiles || [],
      type: patient.type || '',
      gender: patient.metadata?.gender || '',
      patientIDs: this.normalizePatientIDs(patient?.metadata?.medical_record_numbers, patient.caseName),
      metadata: patient.metadata || {}
    };
  }

  buildClientObject(patient) {
    const fullNameFromSingleField = (patient.name || '').toString().trim();
    const [firstFromName, ...restOfName] = fullNameFromSingleField.split(/\s+/);
    const derivedFirst = this.toCapitalCase(firstFromName || '');
    const derivedLast = this.toCapitalCase(restOfName.join(' ') || '');

    const firstName = this.toCapitalCase(patient.FirstName || patient?.metadata?.firstName || derivedFirst);
    const lastName = this.toCapitalCase(patient.LastName || patient?.metadata?.lastName || derivedLast);

    const dobSource = patient.DateOfBirth || patient?.metadata?.dob || patient?.metadata?.date_of_birth;

    return {
      firstname: firstName,
      lastname: lastName,
      caseName: patient.caseName || '',
      gender: patient.metadata?.gender || '',
      patientIDs: this.normalizePatientIDs(patient?.metadata?.medical_record_numbers),
      dob: this.formatDate(dobSource),
      sharedUserFiles: patient.files || [],
      ownerID: patient.ownerID || '',
      owners: patient.ownerID ? [patient.ownerID] : [],
      defaultFolders: patient.defaultFolders || [],
      restrictedProfiles: patient.restrictedProfiles || [],
      allowedProfiles: patient.allowedProfiles || [],
      hasNotifications: this.hasNotifications(patient.caseName)
    };
  }

  /**
   * Get all patients and populate the search result (this.searchResult).
   * @param isConsultant - If the user is a consultant.
   * @param realRole - The role of the user.
   * @param email - The email of the user.
   * @param allowedCaseNames - The allowed case names.
   * @param patientType - The type of the patient.
   */
  async getAllPatients(
    isConsultant: boolean,
    realRole: string,
    email: string,
    allowedCaseNames?: string[],
    patientType?: string
  ) {
    const noCases = allowedCaseNames.length === 0;

    if (!isConsultant && noCases) {
      this.searchResult = [];
      return;
    }

    const chunkSize = 10;

    if ([UserRoles.admin, UserRoles.associate].includes(realRole)) {
      this.searchResult = this.originalData = await this.getInitialData();
    } else if (isConsultant) {
      const patients = (
        allowedCaseNames.length > chunkSize
          ? await this.getPatientsByChunk(allowedCaseNames, chunkSize, patientType)
          : await this.getPatients(allowedCaseNames, patientType)
      ).map(doc => doc.data());

      this.searchResult = patients.map(patient => this.getSearchResult(email, patient, realRole));
    }
    return;
  }

  getSearchResult(email: string, patientData: any, realrole: string) {
    const filtered = patientData.files?.filter((file: { sharedWith: string[] }) =>
      file.sharedWith.map((e: string) => e.toLowerCase()).includes(email.toLowerCase())
    );
    const condition1 = filtered?.length > 0;
    const condition2 = allowedUsers.indexOf(realrole) > -1;
    const condition3 =
      patientData.uidOfCreator === this.currentUser.uid &&
      patientData.ownerID !== '' &&
      this.ownerID === patientData.ownerID;

    if (condition1 || condition2 || condition3) {
      return this.buildClientObject(patientData);
    }
  }

  toCapitalCase(str: string) {
    if (!str || typeof str !== 'string') {
      return '';
    }
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  private formatDate(dateValue: any): string {
    if (!dateValue) {
      return '';
    }

    // If it's already a valid date string, return it
    if (typeof dateValue === 'string' && dateValue.trim() !== '') {
      const date = new Date(dateValue);
      if (!isNaN(date.getTime()) && dateValue !== 'Invalid Date') {
        return dateValue;
      }
    }

    // If it's a Date object
    if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
      return dateValue.toISOString();
    }

    // If it's a timestamp
    if (typeof dateValue === 'number' && !isNaN(dateValue)) {
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        return date.toISOString();
      }
    }

    // Return empty string for any invalid date
    return '';
  }

  /**
   * Normalizes an array of medical record number objects (or primitive values) into a consistent array of objects
   * with `value`, `facility`, and optionally `current` fields.
   * - Each entry in the result describes a patient's medical record ID and its facility.
   * - If a record matches `caseName`, the `current` flag is set to true.
   * - Handles a mix of input formats, providing sensible defaults for missing data.
   *
   * @param medicalRecordNumbers Array of possible record values (could be objects or strings/numbers)
   * @param caseName (optional) Case name to match for marking current record
   * @returns Array of normalized patient ID objects: { value, facility, current? }
   */
  private normalizePatientIDs(
    medicalRecordNumbers: any[],
    caseName?: string
  ): Array<{ value: string; facility: string; current?: boolean }> {
    // Return empty if input is not a valid array
    if (!Array.isArray(medicalRecordNumbers)) {
      return [];
    }

    // Map each entry to a normalized object
    return medicalRecordNumbers.map((record, index) => {
      let value: string, facility: string;

      // If record is an object, attempt to extract value and facility
      if (record && typeof record === 'object') {
        value =
          record.value ??
          (record.number !== undefined ? record.number.toString() : undefined) ??
          record.toString() ??
          `ID-${index}`;
        facility = record.facility || 'default-facility';
      }
      // If it's a primitive (string/number)
      else if (record) {
        value = record.toString();
        facility = 'default-facility';
      }
      // Fallback/default values if record is missing
      else {
        value = `ID-${index}`;
        facility = 'unknown-facility';
      }

      // Mark as current if value matches caseName
      const result: { value: string; facility: string; current?: boolean } = { value, facility };
      if (caseName && value === caseName) {
        result.current = true;
      }

      return result;
    });
  }

  private async getPatients(allowedCaseNames: String[], type: string) {
    if (!allowedCaseNames || allowedCaseNames.length === 0) {
      return [];
    }

    const querySnapshot = await firebase
      .firestore()
      .collection('patients')
      .where('caseName', 'in', allowedCaseNames)
      .where('type', '==', type)
      .get();

    return querySnapshot.docs;
  }

  private async getPatientsByChunk(allowedCaseNames: string[], chunkSize: number, type: string) {
    let patientsArray = [];
    for (let i = 0; i < allowedCaseNames.length; i += chunkSize) {
      const chunk = allowedCaseNames.slice(i, i + chunkSize);
      const patients = await this.getPatients(chunk, type);
      patientsArray = patientsArray.concat(patients);
    }
    return patientsArray;
  }

  private hasNotifications(caseName: string): boolean {
    const userData = this.auth_$.userData.getValue();
    const isConsultant = userData['realRole'] === UserRoles.consultant;

    if (environment.config.notifications === 0) {
      return false;
    }

    let clientsWithNotifications =
      userData['notifications']?.filter(n => n.userId === userData['uid'] && n.caseId === caseName) || [];

    if (isConsultant) {
      clientsWithNotifications = clientsWithNotifications.filter(n => n.type !== 'newfile');
    }

    return clientsWithNotifications.length > 0;
  }

  private getItem(data) {
    const {
      FirstName,
      LastName,
      caseName,
      LegalCaseId,
      DateOfBirth,
      files,
      ownerID,
      defaultFolders,
      Gender,
      patientIDs
    } = data();
    return {
      firstname: this.toCapitalCase(FirstName),
      lastname: this.toCapitalCase(LastName),
      gender: Gender || '',
      patientIDs: patientIDs || [],
      caseName: caseName || '',
      legalCaseId: LegalCaseId || '',
      dob: this.formatDate(DateOfBirth),
      sharedUserFiles: files || [],
      ownerID: ownerID || '',
      owners: ownerID ? [ownerID] : [],
      defaultFolders: defaultFolders || [],
      hasNotifications: this.hasNotifications(caseName)
    };
  }

  private populateSearchResults(results) {
    this.searchResult = [];
    results.docs.forEach(({ data }) => {
      const item = this.getItem(data);
      const result = [];
      result.push(item);

      if (
        this.data.realRole.toString() === UserRoles.superuser ||
        (data().uidOfCreator === this.currentUser.uid && !item.ownerID)
      ) {
        this.searchResult.push(item);
      } else {
        this.searchResult = [];
      }
    });
  }

  getPatientObject(item: any) {
    const metadata = item.metadata || {};

    return {
      clientname: `${item.firstname || ''} ${item.lastname || ''}`.trim(),
      firstname: item.firstname,
      lastname: item.lastname,
      gender: item.gender || '',
      dob: item.dob,
      placeOfBirth: metadata.placeOfBirth || metadata.place_of_birth || '',
      spouseName: metadata.spouseName || metadata.spouse_name || '',
      patientPicture: item.patientPicture || metadata.patientPicture || '',
      caseName: item.caseName,
      ownerID: item.ownerID,
      type: item.type || this.patientType || '',
      metadata,
      patientIDs: item.patientIDs || [],
      LegalCaseId: item.LegalCaseId || item.legalCaseId || '',
      patientDocId: item.patientDocId,
      email: item.email || '',
      phone: item.phone || ''
    };
  }

  get currentFacility(): string {
    return (this.auth_$.userData.getValue() as any)?.facility || '';
  }

  trackByClientId(index: number, client: any): string {
    return client.patientDocId || client.caseName || index.toString();
  }
}
